import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'slab_token';
const USER_KEY = 'slab_user';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Initialize from localStorage for persistent login
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem(USER_KEY);
    return stored ? JSON.parse(stored) : null;
  });
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(true);

  const api = useCallback(() => {
    const instance = axios.create({
      baseURL: API,
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    return instance;
  }, [token]);

  // Verify token on mount and periodically refresh user data
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await api().get('/auth/me');
          setUser(response.data);
          localStorage.setItem(USER_KEY, JSON.stringify(response.data));
        } catch (error) {
          console.error('Auth check failed:', error);
          // Only logout if token is truly invalid (401), not for network errors
          if (error.response?.status === 401) {
            logout();
          }
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token, api]);

  const login = async (email, password, rememberMe = true) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    
    // Store token first
    localStorage.setItem(TOKEN_KEY, access_token);
    setToken(access_token);
    
    // IMPORTANT: Force refresh from /auth/me to get live Stripe subscription status
    // The login response may have stale subscription_active flag
    try {
      const meResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${access_token}` }
      });
      const freshUserData = meResponse.data;
      localStorage.setItem(USER_KEY, JSON.stringify(freshUserData));
      setUser(freshUserData);
      return freshUserData;
    } catch (error) {
      // Fallback to login response data if /auth/me fails
      console.warn('Could not refresh user data after login:', error);
      localStorage.setItem(USER_KEY, JSON.stringify(userData));
      setUser(userData);
      return userData;
    }
  };

  const register = async (email, password, name, username) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, username });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem(TOKEN_KEY, access_token);
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
    
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const response = await api().get('/auth/me');
        setUser(response.data);
        localStorage.setItem(USER_KEY, JSON.stringify(response.data));
      } catch (error) {
        console.error('Refresh user failed:', error);
      }
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      logout,
      api,
      refreshUser,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
